import "./Markdown.scss";
import "../../../theming/streamdown-tailwind.css";
import "streamdown/styles.css";
import { Streamdown, CodeBlockSkeleton, defaultRemarkPlugins, type CustomRendererProps, type MermaidOptions } from "streamdown";
import { code } from "@streamdown/code";
import { createLazyMermaidPlugin } from "./lazy-mermaid-plugin";
import remarkDirective from "remark-directive";
import rehypeRaw from "rehype-raw";
import { mentionsPlugin } from "./remark-plugins/mentions";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import _ from "lodash";
import { JsxRenderer } from "../ContentRenderer/JsxRenderer";
import { DocumentationSample } from "../DocumentationSample";
import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { TableOfContents } from "./TableOfContents";
import { getDataProps } from "../../../shared/utilities";
import { AnchorWrapper } from "../../../shared/components/AnchorWrapper";
import { plainElements, plainStrong, plainInlineCode } from "../../../shared/components/MarkdownElements";
import { useScrollGate } from "../../../shared/hooks";

const getElementId = (text: string) => text.toLowerCase().replace(/[^\w]+/g, '-');

// Extracts the plain text from a rendered children tree.  Recursion matters: children are not
// always plain strings — headings can contain inline markup, and streamdown's `animated` mode
// wraps every streamed word in a span — and heading anchor ids must be derived from the text
// content regardless...
const childrenToText = (children: any): string =>
    _.isArray(children) ? children.map(childrenToText).join("") :
        _.isString(children) ? children :
            _.isNumber(children) ? String(children) :
                children?.props?.children ? childrenToText(children.props.children) :
                    "";

// Custom fenced-language renderers.  `isIncomplete` is true while the fence is still streaming in
// (requires the `streaming` prop), letting us show a placeholder instead of feeding partial JSX to
// the parser...
const QerentBlock = ({ code, isIncomplete }: CustomRendererProps) =>
    isIncomplete ? <CodeBlockSkeleton /> : <JsxRenderer template={code} />;

const DocumentationBlock = ({ code, isIncomplete }: CustomRendererProps) =>
    isIncomplete ? <CodeBlockSkeleton /> : <DocumentationSample input={code} />;

// NOTE: everything passed to <Streamdown> as an object/array/function must be referentially stable
// across renders (module-level or memoized) — its per-block memoization compares these by identity,
// and a changing reference forces every settled block to re-render on each streamed chunk...
const plugins = {
    code,
    mermaid: createLazyMermaidPlugin(),
    renderers: [
        { language: "qerent", component: QerentBlock },
        { language: "documentation", component: DocumentationBlock },
    ],
};

const remarkPlugins = [...Object.values(defaultRemarkPlugins), remarkDirective, mentionsPlugin];

// Parity with the pre-streamdown component: raw HTML passes through unsanitized (all markdown
// sources are trusted template/tenant content).  Passing this explicitly replaces streamdown's
// default rehype-sanitize/rehype-harden pipeline.  NOTE: `allowedTags`/`literalTagContent` are
// implemented inside that default pipeline, so they are inert here — inline <qerent> tags are
// handled via source preprocessing below instead...
const rehypePlugins = [rehypeRaw];

const mermaidOptions: MermaidOptions = {
    config: {
        theme: "neutral",
        themeCSS: ".messageText { fill: var(--colors-blue-2); }",
        fontFamily: "Helvetica Neue",
    }
};

const shikiTheme: [string, string] = ["monokai", "monokai"];

// AnchorWrapper owns link semantics; streamdown's link-safety modal would add a second
// interception layer on top of it...
const linkSafety = { enabled: false };

// Inline <qerent> tags: hoist the tag body into an attribute before parsing, so the markdown/HTML
// pipeline can never mangle it (replaces the legacy source-position extraction, which streamdown's
// per-block parsing would break anyway)...
const encodeInlineQerentTags = (content: any) =>
    _.isString(content) ?
        content.replace(/<qerent>([\s\S]*?)<\/qerent>/g, (_match, inner) => `<qerent template="${encodeURIComponent(inner)}"></qerent>`) :
        content;

type MarkdownProps = {
    name?: string;
    key?: string;
    source?: string;
    children?: any;
    disableAnchorGeneration?: boolean;
    renderTableOfContents?: boolean;
    streaming?: boolean;
    codeBlockMaxHeight?: number;
    lineNumbers?: boolean;
    className?: string;
    [key: string]: any;
}

function Markdown(props: MarkdownProps) {
    const { key, source, children, disableAnchorGeneration, renderTableOfContents, streaming, codeBlockMaxHeight = 0, lineNumbers = false, className, ...rest } = props;

    // Workaround for the fact that JSX Parser wraps whitespace-enclosed elements in a fragment by default...
    const sanitizedChildren = _.isArray(children) ?
        children.find(c => _.isString(c)) :
        children?.type?.toString()?.indexOf("fragment") > -1 ? children?.children?.[0] :
            children;
    const content = encodeInlineQerentTags(source ?? sanitizedChildren);

    // Streamdown's mermaid pan-zoom container attaches a non-passive wheel listener unconditionally
    // (even with controls disabled), hijacking page scroll over diagrams — gate it behind a click...
    const containerRef = useRef<HTMLDivElement>(null);
    useScrollGate(containerRef, `[data-streamdown="mermaid-block"]`);

    const contentRef = useRef<HTMLDivElement>(null);
    const contentElement = contentRef?.current;

    // Force re-render once-off to ensure that contentElement is available for the Table of Contents to attach itself to...
    const [contentElementIsInDom, setContentElementIsInDom] = useState(false);
    useEffect(() => {
        setContentElementIsInDom(true);
    }, []);

    // The hide-until-quiet gate below only applies to one-shot (non-streaming) renders — when
    // streaming, content must be visible from the first chunk.  Captured at mount so a stream
    // completing (streaming flipping to false) doesn't re-hide the content...
    const [initiallyStreaming] = useState(!!streaming);
    const [renderingHasCompleted, setRenderingHasCompleted] = useState(initiallyStreaming);

    useEffect(() => {
        if (!contentElementIsInDom || initiallyStreaming) return;

        // To prevent flickering/scroll-thrashing during the initial render (mermaid + syntax
        // highlighting hydrate asynchronously), we monitor the container element for DOM mutations.
        // Each mutation resets a 100ms timer, so the timer only ever fires once the content has
        // stopped changing — that is our "rendering has completed" signal...
        const onRenderingCompleted = () => {
            setRenderingHasCompleted(true);
            observer.disconnect();

            // Once the content has finished rendering, scroll to the relevant position in the document...
            const hash = window.location.hash.slice(1);
            if (hash) {
                const referencedElement = contentElement.querySelector(`#${hash}`);
                if (referencedElement) {
                    referencedElement.scrollIntoView({ behavior: "instant" });
                    return;
                }
            }

            contentElement?.scrollTo?.({ top: 0, behavior: "instant" });
        };

        const observer = new MutationObserver(() => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(onRenderingCompleted, 100);
        });
        observer.observe(contentElement, { childList: true, subtree: true });
        let timeoutId = setTimeout(onRenderingCompleted, 100);

        return () => {
            observer.disconnect();
            clearTimeout(timeoutId);
        };

    }, [contentElementIsInDom]);

    const components = useMemo(() => {
        const heading = (Tag: "h1" | "h2" | "h3" | "h4" | "h5" | "h6") =>
            ({ node, children, ...props }: any) =>
                <Tag {...props} id={getElementId(childrenToText(children))}>
                    {disableAnchorGeneration ? children : <a href={"#" + getElementId(childrenToText(children))}>{children}</a>}
                </Tag>;

        return {
            ...plainElements,
            qerent: ({ node, template, ...props }: any) => <JsxRenderer template={decodeURIComponent(template ?? "")} {...props} />,
            h1: heading("h1"), h2: heading("h2"), h3: heading("h3"), h4: heading("h4"), h5: heading("h5"), h6: heading("h6"),
            a: ({ node, children, ...props }: any) => <AnchorWrapper {...props}>{children}</AnchorWrapper>,
            strong: plainStrong,
            inlineCode: plainInlineCode,
        };
    }, [disableAnchorGeneration]);

    return (
        <div ref={containerRef} className={clsx("Markdown-container", renderTableOfContents && "with-table-of-contents", className)} style={{ opacity: renderingHasCompleted ? 1 : 0 }} {...getDataProps(props)}>
            <div className="markdown" ref={contentRef}>
                <Streamdown
                    key={key}
                    mode={streaming ? "streaming" : "static"}
                    animated
                    isAnimating={streaming ?? false}
                    plugins={plugins}
                    remarkPlugins={remarkPlugins}
                    rehypePlugins={rehypePlugins}
                    components={components}
                    mermaid={mermaidOptions}
                    shikiTheme={shikiTheme}
                    lineNumbers={lineNumbers}
                    controls={false}
                    codeBlockMaxHeight={codeBlockMaxHeight}
                    tableMaxHeight={0}
                    linkSafety={linkSafety}
                    {...rest}
                >
                    {content}
                </Streamdown>
            </div>
            {renderTableOfContents && contentElementIsInDom && contentElement && <TableOfContents content={content} contentElement={contentElement} />}
        </div>
    );
}

(Markdown as DocumentedComponent).metadata = {
    description: "The `Markdown` component renders the provided markdown as HTML.  It is optimized for streaming content: already-rendered blocks are memoized (no flickering or re-rendering as new chunks arrive) and unterminated markdown is auto-closed while streaming.  See the [component's repository](https://github.com/vercel/streamdown) for details on the underlying renderer.",
    isSelfClosing: false,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "disableAnchorGeneration", type: "boolean", description: "By default, the component will automatically generate URL fragment links for each heading.  This can be disabled by setting this prop to `true`." },
        { name: "renderTableOfContents", type: "boolean", description: "If `true`, the component will render a Table of Contents alongside the markdown content.  Optional - defaults to `false`." },
        { name: "streaming", type: "boolean", description: "Set to `true` to enable incremental rendering for content which streams in (e.g. from an AI/LLM source): content is shown as it arrives, unterminated markdown is auto-closed, and `qerent`/`documentation`/`mermaid` blocks show a placeholder until their fence completes.  Leave set to `true` after the stream completes - changing it back to `false` re-renders the entire content block.  Optional - defaults to `false`." },
        { name: "lineNumbers", type: "boolean", description: "If `true`, fenced code blocks are rendered with line numbers.  Optional - defaults to `false`." },
        { name: "codeBlockMaxHeight", type: "number", description: "Maximum height (in pixels) for fenced code blocks - content beyond this height scrolls within the block.  Set to `0` to disable the height limit.  Optional - defaults to `0` (unlimited)." },
    ]
};

export default Markdown;